import { CountryQueryApi } from '@src/Api/generated-global-event';
import { UserFeedbackCommandApi } from '@src/Api/generated-user-feedback';
import { useStores } from '@src/Common/Hooks/useStores';
import AxiosInstanceGlobalEvent from '@src/Common/Services/Api/AxiosInstanceGlobalEvent';
import AxiosInstanceUserFeedback from '@src/Common/Services/Api/AxiosInstanceUserFeedback';
import { logEvent } from '@src/Domain/Analytic';
import { AnalyticsEvent } from '@src/Domain/Analytic/Analytic.type';
import { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from 'react-oidc-context';

const lDEQa = (AI: AxiosInstance) => new CountryQueryApi(undefined, '', AI);
const lDUFa = (AI: AxiosInstance) =>
  new UserFeedbackCommandApi(undefined, '', AI);
const useDashboard = () => {
  const { UserStore, FormStore, CountryStore, UserFeedbackStore } = useStores();
  const [showContent, setShowcontent] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [showUserFeedbackForm, setShowUserFeedbackForm] =
    useState<boolean>(false);

  // FETCH
  const _fetchCountry = lDEQa(AxiosInstanceGlobalEvent(''));
  const _fetchUserFeedback = lDUFa(AxiosInstanceUserFeedback('user-feedback'));

  const {
    isAuthenticated,
    user,
    signinRedirect,
    signinSilent,
    signoutRedirect,
    removeUser,
    querySessionStatus,
    events
  } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) {
      Cookies.set('redirect_to_dum', 'true', {
        domain:
          process.env.REACT_APP_ENV !== 'local'
            ? 'mythicprotocol.net'
            : 'localhost',
        path: '/'
      });
    } else {
      Cookies.remove('redirect_to_dum', {
        domain:
          process.env.REACT_APP_ENV !== 'local'
            ? 'mythicprotocol.net'
            : 'localhost',
        path: '/'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UserStore.token]);

  useEffect(() => {
    if (user?.access_token) {
      UserStore.loadUserManager();
    }
    CountryStore.fetchCountry(_fetchCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rendered as an anchor `href`, so the browser owns the navigation. Empty
  // when PORTAL_URL is unset, which hides the link instead of sending the user
  // to `undefined`.
  const portalUrl = process.env.PORTAL_URL ?? '';

  const handleToPortal = () => {
    logEvent(
      AnalyticsEvent.LINK_VISITED,
      'DUM Navbar Back To Portal Button Click'
    );
  };

  const onSubmitEvent = async () => {
    if (FormStore.isDetailVisible) {
      // catch if user try to click submit event when add event is already displayed
      return;
    }
    if (isAuthenticated) {
      FormStore.clearCoordinate();
      FormStore.showModal();
      FormStore.isDetailVisible = false;
      logEvent(
        AnalyticsEvent.BUTTON_CLICKED,
        'DUM Navbar Submit Event Button Click'
      );
    } else if (!isAuthenticated) {
      await signinRedirect({
        extraQueryParams: { back_url: `${process.env.PORTAL_URL}` }
      });
    }
  };

  const onBtnSubmitEventClick = async () => {
    setShowSidebar(false);
    if (isAuthenticated) {
      FormStore.showModal();
    } else if (!isAuthenticated) {
      await signinRedirect();
    }
  };

  const onUserFeedbackSubmit = async (value: {
    platform: string;
    topic: string;
    message: string;
  }) => {
    // check recaptcha invisible
    let captchaToken: string | null = null;
    if (recaptchaRef.current) {
      captchaToken = await recaptchaRef?.current.executeAsync();
      if (!captchaToken) {
        UserFeedbackStore.status = {
          isLoading: false,
          isError: false,
          isSuccess: false,
          message: 'Unable to complete the CAPTCHA! Please reload the page'
        };
        return;
      }
    }
    UserFeedbackStore.saveFeedback(_fetchUserFeedback, {
      ...value,
      captchaToken
    });
  };

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) {
        try {
          await querySessionStatus();
          await signinSilent();
          UserStore.loadUserManager();
        } catch (error) {
          await removeUser();
          localStorage.clear();
          UserStore.loadUserManager();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, signinSilent]);

  const handleSignOut = async () => {
    await removeUser();
    localStorage.clear();
    signoutRedirect({
      id_token_hint: user?.id_token,
      extraQueryParams: { back_url: `${process.env.PORTAL_URL}` }
    });
  };

  useEffect(() => {
    if (user?.access_token) {
      UserStore.getAccountData(user?.access_token);
    }
  }, [UserStore, user?.access_token]);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const closeUserFeedbackForm = () => {
    setShowUserFeedbackForm((s) => !s);
    UserFeedbackStore.status = {
      isLoading: false,
      isError: false,
      isSuccess: false,
      message: ''
    };
  };

  return {
    handleSignOut,
    handleToPortal,
    portalUrl,
    onSubmitEvent,
    UserStore,
    FormStore,
    showSidebar,
    setShowSidebar,
    showContent,
    setShowcontent,
    onBtnSubmitEventClick,
    showUserFeedbackForm,
    setShowUserFeedbackForm,
    onUserFeedbackSubmit,
    recaptchaRef,
    closeUserFeedbackForm
  };
};

export default useDashboard;
